import React, { useState } from 'react';
import { FiX, FiStar, FiMessageCircle, FiLoader } from 'react-icons/fi';
import { createCheckoutSession } from '../services/stripeService';

const SubscriptionModal = ({ isOpen, onClose, trialStatus }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  if (!isOpen) return null;

  const { isTrialExpired, daysRemaining } = trialStatus;

  const handleUpgrade = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Starting Stripe checkout process...');
      
      // Create checkout session with 7-day trial
      const checkoutData = await createCheckoutSession({
        // trialPeriodDays: 7
      });
      
      console.log('Checkout session created, redirecting to:', checkoutData.url);
      
      // Redirect to Stripe checkout
      window.location.href = checkoutData.url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.message || 'Failed to start checkout process. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FiStar className="text-yellow-500" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Upgrade to Premium</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800 font-medium">Payment Error</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
            </div>
          )}

          {/* Trial Status Message */}
          <div className={`p-4 rounded-lg mb-6 ${
            isTrialExpired 
              ? 'bg-red-50 border border-red-200' 
              : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <p className={`text-sm font-medium ${
              isTrialExpired ? 'text-red-800' : 'text-yellow-800'
            }`}>
              {isTrialExpired 
                ? 'Your free trial has expired' 
                : `Your free trial expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`}
            </p>
            <p className={`text-xs mt-1 ${
              isTrialExpired ? 'text-red-600' : 'text-yellow-600'
            }`}>
              {isTrialExpired 
                ? 'Upgrade now to continue chatting with Bela'
                : 'Upgrade now to secure unlimited access'}
            </p>
          </div>

          {/* Pricing */}
          <div className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-4 mb-6">
            <div className="text-center">
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span className="text-3xl font-bold text-gray-900">$9.00</span>
                <span className="text-gray-600">/month</span>
              </div>
              <p className="text-sm text-gray-600">
                Cancel anytime • No long-term commitment
              </p>
            </div>
          </div>

          {/* Premium Benefit */}
          <div className="bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-6 mb-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <FiMessageCircle className="text-rose-500" size={24} />
              <h3 className="text-lg font-semibold text-gray-900">Unlimited Conversations</h3>
            </div>
            <p className="text-gray-700">
              Chat with Bela as much as you want, whenever you want. No limits, no restrictions.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleUpgrade}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-sm flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <FiLoader className="animate-spin" size={18} />
                  Processing...
                </>
              ) : (
                'Upgrade to Premium'
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="w-full text-gray-600 hover:text-gray-800 disabled:text-gray-400 text-sm font-medium py-2 transition-colors"
            >
              {isTrialExpired ? 'Not now' : 'Continue with trial'}
            </button>
          </div>

          {/* Footer note */}
          <p className="text-xs text-gray-500 text-center mt-4">
            Secure payment processing
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
